// What carries over from a client's last invoice, and what must not.
//
// Its own module because the rule has real edge cases -- a PERCENT discount,
// an invoice with no lines, last year's year_end -- and testing them through a
// mounted 1,566-line page tests the page. Same reasoning as invoiceStats.js
// and planning/cellFormat.js.

const EMPTY_LINE = { description: "", amount: "" };

// A zero amount renders as "0.00" in an input the user then has to clear.
// Blank reads as "type the fee here", which is what it is.
const lineAmount = (amount) =>
  amount && parseFloat(amount) !== 0 ? String(amount) : "";

const dollars = (value) => {
  const n = parseFloat(value);
  return Number.isFinite(n) && n !== 0 ? String(value) : "";
};

const num = (value) => {
  const n = parseFloat(value);
  return Number.isFinite(n) ? n : 0;
};
const cents = (n) => Math.round(n * 100) / 100;

// The invoice's FEES -- the figure its lines ought to add up to -- read from
// the totals the client was actually billed, not from the lines.
//
// Why not just add up the lines: about 1,250 historical invoices (everything
// loaded before the Lee Humble import) are single-line records whose real
// figures live in the header. fix_invoices.py rewrote subtotal, taxes, total
// and disbursements from the upload CSV after the fact and never touched the
// line -- and its `subtotal` is the CSV's PRE-TAX figure (fee - discount +
// disbursements), so even `subtotal` cannot be trusted as the fee. The total
// and the taxes are the figures that went out, so:
//
//   fees = (total - GST - PST - HST) + discount - disbursements
//
// which, for any invoice ClientZone computed itself, is exactly `subtotal`.
// null when there is no total to read (nothing to reconcile against).
export function feesOf(invoice) {
  if (!invoice || !(num(invoice.total) > 0)) return null;
  const pretax = num(invoice.total) - num(invoice.gst) - num(invoice.pst) - num(invoice.hst);
  const fees = cents(pretax + num(invoice.discount) - num(invoice.disbursements));
  return fees > 0 ? fees : null;
}

// The lines as carried, made to add up to the invoice's fees. A single fee
// line takes the whole fee (the Mertler case: one line, and the draft came up
// one line's amount short of what was billed). With several fee lines nobody
// can say which one was wrong, so the shortfall gets a line of its own, with
// no description -- yellow, and plainly something to look at.
function reconcileLines(lines, invoice) {
  const fees = feesOf(invoice);
  if (fees === null || !lines.length) return lines;
  const sum = cents(lines.reduce((s, l) => s + num(l.amount), 0));
  if (Math.abs(sum - fees) < 0.005) return lines;
  const priced = lines.map((l, i) => (num(l.amount) !== 0 ? i : -1)).filter((i) => i >= 0);
  if (priced.length <= 1) {
    const at = priced.length ? priced[0] : 0;
    return lines.map((l, i) => (i === at ? { ...l, amount: fees.toFixed(2) } : l));
  }
  const shortfall = cents(fees - sum);
  return [...lines, { description: "", amount: shortfall.toFixed(2) }];
}

export function prefillFromInvoice(invoice, { nextNumber, today }) {
  if (!invoice) return {};

  const lines = reconcileLines(
    (invoice.lines || []).map((l) => ({
      description: l.description || "",
      amount: lineAmount(l.amount),
    })),
    invoice,
  );

  return {
    // --- carried: the shape of the bill ---
    lines: lines.length ? lines : [{ ...EMPTY_LINE }],
    tax_mode_ui: invoice.tax_mode || "GST",
    // The serializer's computed DOLLAR discount, never discount_value: this
    // form saves every discount as discount_type AMOUNT, so a PERCENT source
    // read raw would turn "10%" into "$10". See rollup.js's $990 note.
    discount: dollars(invoice.discount),
    disbursements: dollars(invoice.disbursements),
    // Exemptions are facts about the client, so they carry like the tax mode.
    // A 2026 paper invoice recorded its exemption as an amount; any non-zero
    // amount was the whole base (Invoice.is_pst_exempt reads it the same way).
    pst_exempt: Boolean(invoice.pst_exempt) || num(invoice.pst_exempt_amount) > 0,
    gst_exempt: Boolean(invoice.gst_exempt),
    notes: invoice.notes || "",
    bill_to: invoice.bill_to || "",
    care_of: invoice.care_of || "",
    show_care_of: Boolean(invoice.show_care_of),
    ...(invoice.intro_line !== undefined && invoice.intro_line !== null
      ? { intro_line: invoice.intro_line }
      : {}),
    // Not carried: deposits_received and previous_balance. Both belonged to
    // that invoice's day -- a deposit taken then is not taken again.
    prefilled_from: invoice.id ?? null,

    // --- reset: the identity of THIS invoice ---
    date: today,
    number: nextNumber || "",
    status: "DRAFT",
    // Deliberately not invoice.year_end: the source is usually last year's
    // invoice, and carrying its year would tag new work to a closed year.
    year_end: Number(String(today).slice(0, 4)),
    qbo_doc_number: "",
    do_not_sync: false,
  };
}

// The header values a prefill carried, as stored on the draft
// (Invoice.prefill_carried) so the yellow survives a reopen. Lines carry
// their own snapshot (`_carried`, saved as InvoiceLine.carried).
export function carriedHeader(patch) {
  return {
    discount: patch.discount ?? "",
    disbursements: patch.disbursements ?? "",
    tax_mode: patch.tax_mode_ui ?? "",
    pst_exempt: Boolean(patch.pst_exempt),
    gst_exempt: Boolean(patch.gst_exempt),
  };
}

export default prefillFromInvoice;

// The yellow for a draft that was saved WITHOUT a snapshot: every draft made
// before 2026-10-26 (when snapshots started being stored), the 26 loaded from
// paper on 2026-10-02, and any draft typed by hand. Those reopened with no
// marks at all. Here they are compared with `source` -- the client's last
// issued invoice -- read exactly as a prefill from it would have been.
//
// Lines are matched by DESCRIPTION, each source line used once, not by
// position: a line moved, inserted or retyped since then cannot pick up
// another line's fee. A line with no match carries nothing.
export function carriedFromSource(draftLines, source) {
  if (!source) return { lines: draftLines, carried: null };
  const patch = prefillFromInvoice(source, { today: "" });
  const pool = patch.lines.map((l) => ({ ...l, used: false }));
  const lines = draftLines.map((line) => {
    const want = normText(line.description || "");
    const hit = pool.find((p) => !p.used && normText(p.description) === want);
    if (!hit) return line;
    hit.used = true;
    return { ...line, _carried: { description: hit.description, amount: hit.amount } };
  });
  return { lines, carried: carriedHeader(patch) };
}

// Is this field still exactly what the last invoice said? The form paints such
// a field yellow, so a fee or disbursement nobody revisited cannot go out
// again unnoticed. A blank carried value flags nothing -- a blank field is
// already asking to be filled. Amounts compare as numbers so "45" and "45.00"
// agree.
//
// Text compares with its surrounding whitespace and line endings ignored: the
// server TRIMS a line's description on save (DRF CharField), but the carried
// snapshot is JSON and is stored exactly as sent, so a description carried
// with a trailing newline came back without it and lost its yellow on reopen.
const normText = (s) => String(s).replace(/\r\n?/g, "\n").trim();

export function unchangedSinceLast(value, carried) {
  if (carried === undefined || carried === null) return false;
  if (normText(carried) === "") return false;
  if (normText(value) === normText(carried)) return true;
  const a = parseFloat(value);
  const b = parseFloat(carried);
  return /^-?[\d.]+$/.test(String(value).trim()) && Number.isFinite(a) && a === b;
}
